import React, { useRef, useState } from 'react';
import './BulkProgress.css';

// DJB-214: bulk subscribe/unsubscribe feedback for the Curator sections.
//
// The Playlist Subscriptions and Ads & Screensaver Subscriptions sections run
// their "Add All" / "Remove All" / group actions as one request per machine
// (there is no bulk endpoint). Letting every request raise its own toast
// stacked a green box per machine in the top-right corner; instead the run
// shows a progress bar while it works and raises ONE toast when it is done.
//
// `useBulkRun` drives a sequential run and exposes its progress; the caller
// passes `notify: false` to the per-machine context action and reports the
// aggregated result through `describeBulkSubscription`.

// null | { label, done, total } while a run is in flight
export const useBulkRun = () => {
  const [progress, setProgress] = useState(null);
  const runningRef = useRef(false);

  // Resolves to { total, succeeded, failed, failures } once every item has
  // been attempted, or null when nothing ran (empty list / already running).
  // Non-atomic by design: a failing machine doesn't stop the rest.
  const run = async ({ label, items, action }) => {
    const targets = [...new Set(items ?? [])];
    if (runningRef.current || targets.length === 0) return null;
    runningRef.current = true;
    const total = targets.length;
    const failures = [];
    let done = 0;
    setProgress({ label, done, total });
    try {
      for (const item of targets) {
        try {
          await action(item);
        } catch (e) {
          failures.push({ item, message: e?.message ?? String(e) });
        }
        done += 1;
        setProgress({ label, done, total });
      }
    } finally {
      runningRef.current = false;
      setProgress(null);
    }
    return { total, succeeded: total - failures.length, failed: failures.length, failures };
  };

  return { progress, running: progress !== null, run };
};

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

// Single result toast for a finished run. Success: "x machines are
// subscribed/unsubscribed". Any failure turns the box red. Messages stay
// under the Notification component's 50-character cut-off.
export const describeBulkSubscription = ({ total, succeeded, failed }, isSubscribing) => {
  const verb = isSubscribing ? 'subscribed' : 'unsubscribed';
  if (failed === 0) {
    return {
      type: 'success',
      message: `${plural(succeeded, 'machine')} ${succeeded === 1 ? 'is' : 'are'} ${verb}`,
    };
  }
  if (succeeded === 0) {
    return {
      type: 'error',
      message: `Failed to ${isSubscribing ? 'subscribe' : 'unsubscribe'} ${plural(total, 'machine')}`,
    };
  }
  return {
    type: 'error',
    message: `${succeeded} of ${total} machines ${verb}, ${failed} failed`,
  };
};

const BulkProgress = ({ progress }) => {
  if (!progress) return null;
  const { label, done, total } = progress;
  const pct = total ? Math.round((done / total) * 100) : 0;
  return (
    <div
      className="bulk-progress"
      role="progressbar"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={total}
      aria-valuenow={done}
      aria-valuetext={`${done} of ${total}`}
    >
      <div className="bulk-progress-head">
        <span className="bulk-progress-label">{label}</span>
        <span className="bulk-progress-count">
          {done}/{total}
        </span>
      </div>
      <div className="bulk-progress-track" aria-hidden="true">
        <div className="bulk-progress-fill" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

export default BulkProgress;
